import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  MessageCircle,
  Search,
  Target,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  Sparkles,
  ChevronRight,
  ClipboardList,
  Bot,
  RefreshCw,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

// Manual Workflow Phases
const MANUAL_PHASES = [
  {
    id: "conversation",
    name: "Conversation",
    description: "Establish rapport and confirm relevance",
    icon: MessageCircle,
    tasks: [
      { id: "introduce", name: "Introduce yourself and company", priority: "high" },
      { id: "confirm_role", name: "Confirm the lead's role and responsibilities", priority: "high" },
      { id: "confirm_interest", name: "Confirm they are open to discussing the problem", priority: "high" },
      { id: "initial_notes", name: "Capture initial conversation notes", priority: "medium" },
    ]
  },
  {
    id: "discovery",
    name: "Discovery & Requirements",
    description: "Gather business requirements and pain points",
    icon: Search,
    tasks: [
      { id: "current_process", name: "Understand the lead's current process", priority: "high" },
      { id: "pain_points", name: "Identify operational pain points", priority: "high" },
      { id: "capture_requirements", name: "Capture detailed requirements in CRM", priority: "high" },
      { id: "desired_outcomes", name: "Determine desired outcomes and success metrics", priority: "high" },
      { id: "stakeholders", name: "Identify all stakeholders involved", priority: "medium" },
    ]
  },
  {
    id: "qualification",
    name: "Qualification & Opportunity",
    description: "Determine if lead is qualified and ready for opportunity",
    icon: Target,
    tasks: [
      { id: "decision_makers", name: "Identify decision makers and influencers", priority: "high" },
      { id: "budget", name: "Confirm budget range and approval process", priority: "high" },
      { id: "timeline", name: "Understand timeline and urgency", priority: "high" },
      { id: "solution_fit", name: "Confirm our solution fits their needs", priority: "high" },
      { id: "competition", name: "Understand competitive landscape", priority: "medium" },
      { id: "schedule_pm", name: "Schedule meeting with Product Manager", priority: "high" },
      { id: "create_opportunity", name: "Convert to Opportunity", priority: "high" },
    ]
  }
];

export default function ManualLeadWorkflowPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [activePhase, setActivePhase] = useState("conversation");

  const token = localStorage.getItem("admin_token") || localStorage.getItem("adminToken") || localStorage.getItem("token");

  useEffect(() => {
    if (token && leadId) {
      fetchLeadAndWorkflow();
    }
  }, [leadId, token]);

  const fetchLeadAndWorkflow = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        
        // Check if manual_workflow exists and has valid phases
        if (data.manual_workflow && data.manual_workflow.phases && data.manual_workflow.phases.length > 0) {
          setWorkflow(data.manual_workflow);
          // Set active phase to first incomplete phase
          const firstIncomplete = data.manual_workflow.phases.find(p => p.status !== "completed");
          if (firstIncomplete) {
            setActivePhase(firstIncomplete.id);
          }
        } else {
          // Initialize workflow
          const initialWorkflow = {
            started_at: new Date().toISOString(),
            phases: MANUAL_PHASES.map(phase => ({
              id: phase.id,
              name: phase.name,
              status: phase.id === "conversation" ? "in_progress" : "pending",
              tasks: phase.tasks.map(task => ({ ...task, completed: false }))
            }))
          };
          
          // Save first, then set state
          try {
            const saveResponse = await fetch(`${API}/admin/leads/${leadId}/manual-workflow`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(initialWorkflow),
            });
            if (saveResponse.ok) {
              setWorkflow(initialWorkflow);
            } else {
              console.error("Failed to save initial workflow");
              setWorkflow(initialWorkflow); // Still set locally
            }
          } catch (err) {
            console.error("Error saving initial workflow:", err);
            setWorkflow(initialWorkflow); // Still set locally
          }
        }
      }
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Failed to load lead data");
    } finally {
      setLoading(false);
    }
  };

  const saveWorkflow = async (workflowData = workflow) => {
    try {
      await fetch(`${API}/admin/leads/${leadId}/manual-workflow`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(workflowData),
      });
    } catch (error) {
      console.error("Error saving workflow:", error);
    }
  };

  const handleTaskToggle = async (phaseId, taskId) => {
    if (!workflow || !workflow.phases) {
      toast.error("Workflow not initialized");
      return;
    }
    
    const updatedWorkflow = { ...workflow };
    const phase = updatedWorkflow.phases.find(p => p.id === phaseId);
    if (!phase) {
      toast.error("Phase not found");
      return;
    }
    
    const task = phase.tasks.find(t => t.id === taskId);
    if (!task) {
      toast.error("Task not found");
      return;
    }
    
    task.completed = !task.completed;
    task.completed_at = task.completed ? new Date().toISOString() : null;

    // Check if all tasks in phase are complete
    const allComplete = phase.tasks.every(t => t.completed);
    if (allComplete) {
      phase.status = "completed";
      phase.completed_at = new Date().toISOString();
      
      // Auto-advance to next phase
      const phaseIndex = updatedWorkflow.phases.findIndex(p => p.id === phaseId);
      if (phaseIndex < updatedWorkflow.phases.length - 1) {
        const nextPhase = updatedWorkflow.phases[phaseIndex + 1];
        nextPhase.status = "in_progress";
        setActivePhase(nextPhase.id);
        toast.success(`Phase "${phase.name}" completed! Moving to "${nextPhase.name}"`);
      } else {
        toast.success("All phases completed! Lead is ready for conversion.");
      }
    } else {
      phase.status = "in_progress";
    }

    setWorkflow(updatedWorkflow);
    await saveWorkflow(updatedWorkflow);
  };

  const getPhaseProgress = (phaseId) => {
    const phase = workflow?.phases?.find(p => p.id === phaseId);
    if (!phase) return 0;
    const completed = phase.tasks.filter(t => t.completed).length;
    return Math.round((completed / phase.tasks.length) * 100);
  };

  const getTotalProgress = () => {
    if (!workflow?.phases) return 0;
    const totalTasks = workflow.phases.reduce((acc, p) => acc + p.tasks.length, 0);
    const completedTasks = workflow.phases.reduce((acc, p) => acc + p.tasks.filter(t => t.completed).length, 0);
    return Math.round((completedTasks / totalTasks) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/admin/leads/${leadId}`)}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {lead?.first_name}'s Profile
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Manual Lead Workflow</h1>
              <p className="text-zinc-400">{lead?.first_name} {lead?.last_name} - {lead?.company}</p>
            </div>
          </div>
          
          {/* Overall Progress */}
          <div className="flex items-center gap-6">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLeadAndWorkflow}
              disabled={loading}
              className="border-zinc-700 text-zinc-300 hover:text-white"
              data-testid="refresh-workflow-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <div className="text-right">
              <p className="text-sm text-zinc-400">Overall Progress</p>
              <p className="text-2xl font-bold text-blue-400">{getTotalProgress()}%</p>
            </div>
            <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                style={{ width: `${getTotalProgress()}%` }}
              />
            </div>
          </div>
        </div>

        {/* AI Update Banner */}
        {workflow?.last_ai_update && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30" data-testid="ai-update-banner">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Bot className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-300">
                  ZOEY AI automatically updated {workflow.last_ai_update_tasks?.length || 0} task(s)
                </p>
                <p className="text-xs text-zinc-400">
                  Last AI analysis: {new Date(workflow.last_ai_update).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {workflow.last_ai_update_tasks?.slice(0, 3).map(taskId => (
                  <span key={taskId} className="px-2 py-1 rounded bg-purple-500/20 text-purple-300 text-xs">
                    {taskId.replace(/_/g, " ")}
                  </span>
                ))}
                {workflow.last_ai_update_tasks?.length > 3 && (
                  <span className="px-2 py-1 rounded bg-zinc-700 text-zinc-300 text-xs">
                    +{workflow.last_ai_update_tasks.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Phase Navigation */}
        <div className="flex items-center justify-between mb-8 bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          {MANUAL_PHASES.map((phase, idx) => {
            const PhaseIcon = phase.icon;
            const phaseData = workflow?.phases?.find(p => p.id === phase.id);
            const isComplete = phaseData?.status === "completed";
            const isActive = activePhase === phase.id;
            const isInProgress = phaseData?.status === "in_progress";
            
            return (
              <div key={phase.id} className="flex items-center">
                <button
                  onClick={() => setActivePhase(phase.id)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    isActive 
                      ? "bg-blue-500/20 border border-blue-500/50" 
                      : isComplete
                      ? "bg-green-500/10 border border-green-500/30"
                      : "hover:bg-zinc-800"
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    isComplete ? "bg-green-500/20" : 
                    isInProgress ? "bg-blue-500/20" : "bg-zinc-800"
                  }`}>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <PhaseIcon className={`w-5 h-5 ${isInProgress ? "text-blue-400" : "text-zinc-500"}`} />
                    )}
                  </div>
                  <div className="text-left">
                    <p className={`font-medium ${isActive ? "text-white" : "text-zinc-400"}`}>
                      {phase.name}
                    </p>
                    <p className="text-xs text-zinc-500">{getPhaseProgress(phase.id)}% complete</p>
                  </div>
                </button>
                {idx < MANUAL_PHASES.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-zinc-600 mx-2" />
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Phase Tasks */}
          <div className="col-span-2 space-y-6">
            {MANUAL_PHASES.filter(p => p.id === activePhase).map(phase => {
              const PhaseIcon = phase.icon;
              const phaseData = workflow?.phases?.find(p => p.id === phase.id);
              
              return (
                <div key={phase.id} className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <PhaseIcon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">{phase.name}</h2>
                      <p className="text-sm text-zinc-400">{phase.description}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {phase.tasks.map(task => {
                      const taskData = phaseData?.tasks?.find(t => t.id === task.id);
                      const isCompleted = taskData?.completed;
                      const isAICompleted = taskData?.completed_by?.includes("ZOEY AI");
                      
                      return (
                        <div
                          key={task.id}
                          data-testid={`task-${task.id}`}
                          className={`flex items-center gap-3 p-4 rounded-lg border transition-all cursor-pointer ${
                            isCompleted 
                              ? isAICompleted
                                ? "bg-purple-500/10 border-purple-500/30"
                                : "bg-green-500/10 border-green-500/30" 
                              : "bg-zinc-800/50 border-zinc-700/50 hover:border-blue-500/30"
                          }`}
                          onClick={() => handleTaskToggle(phase.id, task.id)}
                        >
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            isCompleted 
                              ? isAICompleted 
                                ? "border-purple-500 bg-purple-500"
                                : "border-green-500 bg-green-500" 
                              : "border-zinc-600"
                          }`}>
                            {isCompleted && (
                              isAICompleted 
                                ? <Bot className="w-3 h-3 text-white" />
                                : <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${
                                isCompleted 
                                  ? isAICompleted ? "text-purple-400" : "text-green-400" 
                                  : "text-white"
                              }`}>
                                {task.name}
                              </p>
                              {isAICompleted && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs">
                                  <Sparkles className="w-3 h-3" />
                                  AI Detected
                                </span>
                              )}
                            </div>
                            {taskData?.completed_at && (
                              <p className="text-xs text-zinc-500">
                                {isAICompleted ? "Auto-completed by ZOEY • " : "Completed "}
                                {new Date(taskData.completed_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            task.priority === "high" ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Column - Quick Actions & Notes */}
          <div className="space-y-6">
            {/* ZOEY AI Actions */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <h3 className="font-medium">ZOEY AI Actions</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/call`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all"
                >
                  <Phone className="w-5 h-5 text-emerald-400" />
                  <span className="text-xs text-zinc-300">AI Call</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/email`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-blue-500/30 transition-all"
                >
                  <Mail className="w-5 h-5 text-blue-400" />
                  <span className="text-xs text-zinc-300">AI Email</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/text`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-purple-500/30 transition-all"
                >
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  <span className="text-xs text-zinc-300">AI Text</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/appointment`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-all"
                >
                  <Calendar className="w-5 h-5 text-orange-400" />
                  <span className="text-xs text-zinc-300">Schedule</span>
                </button>
              </div>
            </div>

            {/* Lead Info */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <h3 className="font-medium mb-4">Lead Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Company</span>
                  <span className="text-white text-sm">{lead?.company || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Email</span>
                  <span className="text-white text-sm truncate ml-4">{lead?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Phone</span>
                  <span className="text-white text-sm">{lead?.mobile_phone || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Status</span>
                  <span className="text-blue-400 text-sm">{lead?.status}</span>
                </div>
              </div>
            </div>

            {/* Convert to Opportunity */}
            {getTotalProgress() >= 80 && (
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                <h3 className="font-medium text-green-400 mb-2">Ready for Conversion!</h3>
                <p className="text-sm text-zinc-400 mb-4">
                  This lead has completed most qualification tasks and may be ready to convert to an Opportunity.
                </p>
                <Button
                  onClick={() => navigate(`/admin/leads/${leadId}`)}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  Convert to Opportunity
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
